import { useEffect, useState } from "react";
import { MapContainer, TileLayer, GeoJSON, ImageOverlay, Polyline, Polygon, CircleMarker, Marker, Tooltip, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";

// fix default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

export const BASEMAPS = {
  dark: { name: "Dark", url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", attr: "© CARTO" },
  light: { name: "Light", url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", attr: "© CARTO" },
  osm: { name: "Street", url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attr: "© OpenStreetMap" },
  satellite: { name: "Satellite", url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attr: "© Esri" },
  terrain: { name: "Terrain", url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png", attr: "© OpenTopoMap" },
};

function FitBounds({ boundary, bbox, onFit }) {
  const map = useMap();
  useEffect(() => {
    let done = false;
    if (boundary) {
      try {
        const layer = L.geoJSON(boundary);
        map.fitBounds(layer.getBounds(), { padding: [30, 30] });
        done = true;
      } catch (_) { /* ignore */ }
    }
    if (!done && bbox) map.fitBounds([[bbox[1], bbox[0]], [bbox[3], bbox[2]]], { padding: [30, 30] });
    if (onFit) onFit(map.getZoom());
  }, [boundary, bbox, map]);
  return null;
}

function ZoomWatcher({ onZoom }) {
  const map = useMapEvents({ zoomend() { onZoom(map.getZoom()); } });
  return null;
}

function DrawHandler({ active, onAddPoint }) {
  useMapEvents({
    click(e) {
      if (active) onAddPoint([e.latlng.lng, e.latlng.lat]);
    },
  });
  return null;
}

export default function MapView({
  boundary, bbox, basemap = "dark", overlay, overlayOpacity = 0.75,
  targets = [], drawActive = false, drawPoints = [], onAddPoint, height = "100%", center = [-2.85, 32.25],
  samplePoints = [], planPoints = [], planRoutes = [], lineamentFeatures = [],
}) {
  const bm = BASEMAPS[basemap] || BASEMAPS.dark;
  const targetLabel = (id, i) => "T" + String(i + 1).padStart(2, "0");
  const overlayBounds = overlay?.bbox ? [[overlay.bbox[1], overlay.bbox[0]], [overlay.bbox[3], overlay.bbox[2]]] : null;
  const sampleColor = (t) => ({ "rock-chip": "#e63946", soil: "#e9c46a", "drill-collar": "#8b5cf6" }[t] || "#2a9d8f");
  const linColor = (p) => p?.status === "rejected" ? "#64748b" : p?.status && p.status !== "proposed" ? "#22d3ee" :
    (p?.class || "").includes("Major") ? "#e63946" : "#f59e0b";
  const vals = samplePoints.map((s) => s.value).filter((v) => v != null);
  const vMin = vals.length ? Math.min(...vals) : 0, vMax = vals.length ? Math.max(...vals) : 1;
  const gradeColor = (v) => {
    if (v == null) return "#94a3b8";
    const t = vMax > vMin ? (v - vMin) / (vMax - vMin) : 0.5;
    const stops = [[42, 157, 143], [233, 196, 106], [230, 57, 70]];
    const i = t < 0.5 ? 0 : 1, f = t < 0.5 ? t * 2 : (t - 0.5) * 2;
    const c = stops[i].map((a, k) => Math.round(a + (stops[i + 1][k] - a) * f));
    return `rgb(${c[0]},${c[1]},${c[2]})`;
  };
  const gradeRadius = (v) => v == null ? 4 : 4 + 5 * (vMax > vMin ? (v - vMin) / (vMax - vMin) : 0.5);
  const prioColor = (p) => p === "Priority 1" ? "#e63946" : p === "Priority 2" ? "#e9c46a" : "#2a9d8f";
  const prioAbbr = (p) => p === "Priority 1" ? "P1" : p === "Priority 2" ? "P2" : "P3";
  const [zoom, setZoom] = useState(0);
  const [fitZoom, setFitZoom] = useState(99);
  const showLabels = zoom >= fitZoom + 1;
  return (
    <div style={{ height }} className="w-full relative">
      <MapContainer preferCanvas center={center} zoom={9} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
        <TileLayer url={bm.url} attribution={bm.attr} subdomains={bm.url.includes("cartocdn") ? "abcd" : "abc"} />
        {boundary && <GeoJSON key={JSON.stringify(boundary)} data={boundary} style={{ color: "#38bdf8", weight: 4, opacity: 1, fillOpacity: 0.05 }} />}
        {overlay?.png && overlayBounds && (
          <ImageOverlay url={overlay.png} bounds={overlayBounds} opacity={overlayOpacity} zIndex={500} />
        )}
        {targets.map((t, i) =>
          t.polygon && t.polygon.length > 2 ? (
            <Polygon key={"tz" + (t.target_id || i)} positions={t.polygon.map((c) => [c[1], c[0]])}
              pathOptions={{ color: prioColor(t.priority), weight: 2.5, fillColor: prioColor(t.priority), fillOpacity: 0.2 }}>
              {showLabels && (
                <Tooltip permanent direction="center" className="sgex-target-label" opacity={1}>
                  {targetLabel(t.target_id, i)} · {prioAbbr(t.priority)}
                </Tooltip>
              )}
            </Polygon>
          ) : null
        )}
        {targets.map((t, i) =>
          t.centroid ? (
            <CircleMarker
              key={t.target_id || i}
              center={[t.centroid.lat, t.centroid.lon]}
              radius={t.priority === "Priority 1" ? 8 : t.priority === "Priority 2" ? 6.5 : 5}
              pathOptions={{ color: "#e2e8f0", weight: 2, fillColor: "#0ea5e9", fillOpacity: 0.9 }}
            >
              {showLabels && !(t.polygon && t.polygon.length > 2) && (
                <Tooltip permanent direction="top" offset={[0, -8]} className="sgex-target-label" opacity={1}>
                  {targetLabel(t.target_id, i)} · {prioAbbr(t.priority)}
                </Tooltip>
              )}
              <Popup>
                <div className="text-xs leading-relaxed" style={{ minWidth: 150 }}>
                  <div className="font-semibold">{t.target_id}</div>
                  <div>{t.priority} · {t.commodity}</div>
                  <div>Prospectivity {t.prospectivity_score}</div>
                  {t.confidence_score != null && <div>Confidence {t.confidence_score}%</div>}
                  <div>{t.centroid.lat}, {t.centroid.lon}</div>
                  {t.rationale && <div className="mt-1 opacity-80">{t.rationale}</div>}
                  {t.interpretation && <div className="mt-1 opacity-80">{String(t.interpretation).slice(0, 220)}</div>}
                </div>
              </Popup>
            </CircleMarker>
          ) : null
        )}
        {drawPoints.map((p, i) => <Marker key={i} position={[p[1], p[0]]} />)}
        {drawPoints.length >= 2 && (
          <Polyline positions={drawPoints.map((c) => [c[1], c[0]])} pathOptions={{ color: "#e9c46a", weight: 2, dashArray: "6" }} />
        )}
        {lineamentFeatures.filter((f) => f.properties?.status !== "rejected").map((f, i) => (
          <Polyline key={"ln" + i} positions={f.geometry.coordinates.map((c) => [c[1], c[0]])}
            pathOptions={{ color: linColor(f.properties), weight: (f.properties?.class || "").includes("Major") ? 2.5 : 1.5 }}>
            <Tooltip>{f.properties?.id} · {f.properties?.class} · {f.properties?.status}</Tooltip>
          </Polyline>
        ))}
        {planRoutes.map((r, i) => (
          <Polyline key={"pr" + i} positions={r.coordinates.map((c) => [c[1], c[0]])} pathOptions={{ color: "#22d3ee", weight: 1.5, dashArray: "4" }} />
        ))}
        {planPoints.map((p, i) => (
          <CircleMarker key={"pp" + i} center={[p.lat, p.lon]} radius={3} pathOptions={{ color: "#22d3ee", fillColor: "#22d3ee", fillOpacity: 0.9, weight: 0.5 }} />
        ))}
        {samplePoints.map((s, i) => (
          <CircleMarker key={"sp" + i} center={[s.lat, s.lon]} radius={s.value != null ? gradeRadius(s.value) : 5}
            pathOptions={{ color: "#0b1220", weight: 1, fillColor: s.value != null ? gradeColor(s.value) : sampleColor(s.type), fillOpacity: 0.9 }}>
            <Tooltip>{s.sample_id} · {s.type}{s.value != null ? ` · ${s.value} ${s.unit || ""}` : ""}</Tooltip>
          </CircleMarker>
        ))}
        <DrawHandler active={drawActive} onAddPoint={onAddPoint} />
        <ZoomWatcher onZoom={setZoom} />
        <FitBounds boundary={boundary} bbox={bbox} onFit={(z) => { setFitZoom(z); setZoom(z); }} />
      </MapContainer>
    </div>
  );
}
